import {
  JobTypeMResponse,
  JobTypeSResponse,
  JobTypeLResponse,
  IndustrySResponse,
  StationResponse,
} from '@doda/api-client/aspida/@types';
import { PLP_COMMA_PAGE_LIST } from '@doda/common/src/constants/seo/plpCommaPageList';
import { AppQueryParams, Item } from '../../constants/queryParams';
import {
  DODA_OFFER_SP,
  DODA_SEARCH_RESULT_DETAIL,
  DODA_SEARCH_RESULT_LEFTPANEL_AR,
  DODA_SEARCH_RESULT_LEFTPANEL_IND,
  DODA_SEARCH_RESULT_LEFTPANEL_NO,
  DODA_SEARCH_RESULT_LEFTPANEL_OC,
  KYUJIN_FEATURE_TITLE_NM,
  DODA_SEARCH_TOP,
  NEAR_AREA_TEXT_PR,
  NEAR_AREA_TEXT_CIWA,
  SIGN_FULLSIZE_KAKO_ST,
  SIGN_FULLSIZE_KAKO_ED,
} from '../../constants/seo';
import { DODA_SITE_URL } from '../../constants/urls';
import { getSearchScreenKind } from '../seo';
import { ID_OTHERS } from '../../constants/properties/const';
import MasterCacheModel, {
  PrefOfSearchModal,
} from '../../models/MasterCacheModel';
import {
  SeoTextWardResponse,
  SeoTextRouteResponse,
} from '../../interfaces/api/master/seo';
import {
  OP_SEARCH_ITEMS,
  HA_SEARCH_ITEMS,
  NE_SEARCH_ITEMS,
  ES_SEARCH_ITEMS,
} from '../../constants/searchItems';
import { ownerOp, sortOp } from '../../utils/opUtil';
import {
  haSelectCheck,
  isTwoParamPreK,
  isTwoParamOpK,
  isTwoParamHaK,
  isTwoParamNeK,
  isTwoParamEsK,
  isEmptyArray,
  valueExistsInOp,
  getPlpCommaSummaryPageIndex,
} from '../../utils/seoUtil';

/**
 * Seoテキスト作成
 * @param query  AppQueryParams パラメータデータ
 * @param canonicalUrl  canonicalUrl
 * @returns outDto
 */
export const createSeoText = (
  query: AppQueryParams,
  canonicalUrl: string
): Dto => {
  const {
    nearArea,
    baseArea,
    k,
    ind,
    oc,
    ar,
    pr,
    ci,
    wa,
    rt,
    st,
    ha,
    ne,
    es,
    qc,
    featuredJobNm,
  } = query;

  const op = sortOp(ownerOp(query.op));

  const screenKind = getSearchScreenKind(
    DODA_SITE_URL.jobSearchList,
    query,
    {}
  );

  let outDto: Dto = {
    titleOrText: '',
    text: '',
    areaText: '',
    careerExplainText: '',
    defaultFlg: false,
  };

  const isJobSearchListScreen = !qc;

  switch (screenKind) {
    case DODA_SEARCH_RESULT_LEFTPANEL_OC:
      outDto = jobDrilldown(outDto, query);
      break;
    case DODA_SEARCH_RESULT_LEFTPANEL_AR:
      outDto = areaDrilldown(outDto, query);
      break;
    case DODA_SEARCH_RESULT_LEFTPANEL_IND:
      outDto = indDrilldown(outDto, query);
      break;
    case DODA_SEARCH_RESULT_LEFTPANEL_NO:
      // こだわり条件
      if (
        isOnlyForArray(op || [], [
          k,
          ind,
          oc,
          ar,
          pr,
          ci,
          wa,
          rt,
          st,
          ha,
          ne,
          es,
        ]) &&
        dodaKodawariMap().get(getArrayFirst(op))?.length
      ) {
        outDto.titleOrText = dodaKodawariMap().get(getArrayFirst(op));
      } else if (
        // キーワード
        isOnlyForArray(k, [ind, oc, ar, pr, ci, wa, rt, st, op, ha, ne, es])
      ) {
        outDto.titleOrText = sanitize(k || '');
      } else if (
        // 年収
        isOnlyForArray([getArrayFirst(ha)] || [], [
          k,
          ind,
          oc,
          ar,
          pr,
          ci,
          wa,
          rt,
          st,
          op,
          ne,
          es,
        ]) &&
        dodaNenshuMap().get(getArrayFirst(ha))?.length &&
        haSelectCheck(ha)
      ) {
        outDto.titleOrText = dodaNenshuMap().get(getArrayFirst(ha));
      } else if (
        // 従業員数
        isOnlyForArray(ne || [], [
          k,
          ind,
          oc,
          ar,
          pr,
          ci,
          wa,
          rt,
          st,
          op,
          ha,
          es,
        ]) &&
        dodaJugyoinMap().get(getArrayFirst(ne))?.length
      ) {
        outDto.titleOrText = dodaJugyoinMap().get(getArrayFirst(ne));
      } else if (
        // 設立年数
        isOnlyForArray(es || [], [
          k,
          ind,
          oc,
          ar,
          pr,
          ci,
          wa,
          rt,
          st,
          op,
          ha,
          ne,
        ]) &&
        dodaSetsuritsuMap().get(getArrayFirst(es))?.length
      ) {
        outDto.titleOrText = dodaSetsuritsuMap().get(getArrayFirst(es));
      } else if (
        // 路線
        isOnlyForArray(rt || [], [k, ind, oc, ar, ci, wa, es, st, op, ha, ne])
      ) {
        outDto = setRouteSentence(outDto, getArrayFirst(rt));
      } else if (isTwoParamOpOp(query)) {
        // こだわり条件が二つある。
        if (!op) return outDto;
        outDto.titleOrText = `${dodaKodawariMap().get(
          op[0] || ''
        )}、${dodaKodawariMap().get(op[1] || '')}`;
      } else if (isThreeParamOpOpOp(query)) {
        // こだわり条件３つ選択
        // selected three op conditions
        if (!op) return outDto;
        outDto.titleOrText = `${dodaKodawariMap().get(
          op[0] || ''
        )}、${dodaKodawariMap().get(op[1] || '')}、${dodaKodawariMap().get(
          op[2] || ''
        )}`;
      } else if (isTwoParamOpHa(query)) {
        // こだわり条件１つと年収選択
        // selected one op and ha conditions
        if (!op || !ha) return outDto;
        if (haSelectCheck(ha)) {
          outDto.titleOrText = `${dodaKodawariMap().get(
            op[0] || ''
          )}、${dodaNenshuMap().get(ha[0] || '')}`;
        }
      } else if (isThreeParamOpOpHa(query)) {
        // こだわり条件２つと年収選択
        // selected two op and ha conditions
        if (!op || !ha) return outDto;
        if (haSelectCheck(ha)) {
          outDto.titleOrText = `${dodaKodawariMap().get(
            op[0] || ''
          )}、${dodaKodawariMap().get(op[1] || '')}、${dodaNenshuMap().get(
            ha[0] || ''
          )}`;
        }
      } else if (op && isTwoParamOpK(query)) {
        // (op × k)
        outDto.titleOrText = `${dodaKodawariMap().get(op[0])}${isJobSearchListScreen ? `、${k}` : ''}`;
      } else if (ha && isTwoParamHaK(query)) {
        // (ha × k)
        outDto.titleOrText = `${dodaNenshuMap().get(ha[0])}${isJobSearchListScreen ? `、${k}` : ''}`;
      } else if (isJobSearchListScreen && ne && isTwoParamNeK(query)) {
        // (ne × k)
        outDto.titleOrText = `${dodaJugyoinMap().get(ne[0])}、${k}`;
      } else if (isJobSearchListScreen && es && isTwoParamEsK(query)) {
        // (es × k)
        outDto.titleOrText = `${dodaSetsuritsuMap().get(es[0])}、${k}`;
      }
      break;
    case DODA_SEARCH_RESULT_DETAIL:
      // empty
      break;
    case DODA_SEARCH_TOP:
      // empty
      break;
    case DODA_OFFER_SP:
      outDto.defaultFlg = true;
      if (!featuredJobNm?.length) {
        outDto.titleOrText = KYUJIN_FEATURE_TITLE_NM;
      } else {
        outDto.titleOrText = featuredJobNm;
      }
      break;
    // 資格画面用の分岐
    // case DODA_CAREERRECOMMENDJOB_LIST:
    //   let h1nm = '';
    //   let h1Param = '';
    //   if (isTwoParamQcPr(query)) {
    //     // TODO: 都道府県名を取得
    //   } else if (isTwoParamQcCi(query)) {
    //     // TODO: 市区町村名を取得
    //   } else if (isTwoParamQcOp(query) && op) {
    //     // TODO: こだわり名を取得
    //     h1nm = dodaKodawariMap().get(op[0]);
    //     h1Param = `${h1nm}の`;
    //   }

    //   const { qc } = query;
    //   //
    //   break;
    default:
      break;
  }

  // PLPまとめページ対象の場合
  if (setOutDtoForPlpCommaPage(outDto, canonicalUrl) > -1) {
    return outDto;
  }

  // ドリルダウンの種別に関わらず、近隣検索の場合はタイトルテキストを設定
  if (nearArea) {
    if (pr?.length) {
      const prefectureList = createPrefectureList();
      outDto.titleOrText =
        prefectureList.find((x) => x.id === baseArea)?.name || '';
      outDto.areaText = NEAR_AREA_TEXT_PR;
    } else if (ci?.length) {
      const cityList = MasterCacheModel?.city || [];
      outDto.titleOrText = cityList.find((x) => x.id === baseArea)?.name || '';
      outDto.areaText = NEAR_AREA_TEXT_CIWA;
    } else if (wa?.length) {
      const wardList = createWandList();
      outDto.titleOrText = wardList.find((x) => x.id === baseArea)?.name || '';
      outDto.areaText = NEAR_AREA_TEXT_CIWA;
    } else if (st?.length) {
      const stationList = createStationList();
      const station = stationList.find((x) => x.id === baseArea);
      if (!station) return outDto;
      const prefectureList = createPrefectureList();
      const matchedPrefecture = prefectureList.find(
        (pref) =>
          // eslint-disable-next-line no-useless-escape
          pref.id === station.prefectureId && !/\(([^\)]*)\)/.test(station.name)
      );
      outDto.titleOrText = matchedPrefecture
        ? station.name + createBracketedText(matchedPrefecture.name)
        : station.name;
      outDto.areaText = '駅';
    }
  }

  // タイトルテキストに何も設定されていない場合、デフォルト値を設定
  if (outDto?.titleOrText?.length === 0) {
    outDto.defaultFlg = true;
    outDto.titleOrText = '転職・求人情報検索';
  }

  return outDto;
};

/**
 * interface
 * @param titleOrText タイトルORテキスト
 * @param text テキスト
 * @param areaText 近隣検索用のエリア文言
 * @param careerExplainText 等級表示用の文言
 * @param defaultFlg
 */
export interface Dto {
  titleOrText: string;
  text: string;
  areaText: string;
  careerExplainText: string;
  defaultFlg: boolean;
}

/**
 * PLPまとめページの場合、outDtoセット
 * @param outDto Dto
 * @param canonicalUrl  canonicalUrl
 * @returns index
 */
const setOutDtoForPlpCommaPage = (outDto: Dto, canonicalUrl: string) => {
  const index = getPlpCommaSummaryPageIndex(canonicalUrl);
  if (index > -1) {
    outDto.titleOrText = PLP_COMMA_PAGE_LIST[index].commaPageName;
  }
  return index;
};

/**
 * 職種ドリルダウン
 * @param outDto Dto
 * @param query  AppQueryParams パラメータデータ
 * @returns outDto
 */
const jobDrilldown = (outDto: Dto, query: AppQueryParams) => {
  const { oc } = query;
  if (oc?.length && oc.length === 1) {
    jobLogic(outDto, query);
  }
  return outDto;
};

/**
 * エリアドリルダウン
 * @param outDto Dto
 * @param query  AppQueryParams パラメータデータ
 * @returns outDto
 */
const areaDrilldown = (outDto: Dto, query: AppQueryParams): Dto => {
  const { ar, pr, ci, wa, rt, st } = query;
  if (
    !!ar?.length &&
    !pr?.length &&
    !ci?.length &&
    !wa?.length &&
    !st?.length &&
    !rt?.length
  ) {
    if (ar.length === 1) {
      // 地域が1つのみ選択されていた場合
      const areaList = MasterCacheModel?.area || [];
      const area = areaList.find((x) => x.id === getArrayFirst(ar));
      if (!area) return outDto;
      const threePfnm = area.prefectureList
        .slice(0, 2)
        .map((x) => x.name)
        .join();
      outDto.titleOrText = area.name.replace(/^九州$/, '九州・沖縄');
      outDto.text = threePfnm;
    }
    outDto = jobLogic(outDto, query);
  }
  if (
    !!pr?.length &&
    !ar?.length &&
    !ci?.length &&
    !wa?.length &&
    !st?.length &&
    !rt?.length
  ) {
    if (pr.length === 1) {
      // 都道府県が1つのみ選択されていた場合
      const cityList = MasterCacheModel?.city || [];
      const prefectureList = createPrefectureList();
      const pref = prefectureList.find((x) => x.id === getArrayFirst(pr));
      if (!pref) return outDto;
      outDto.titleOrText = pref.name;
      outDto.text = cityList
        .filter((x) => x.prefId === getArrayFirst(pr))
        .slice(0, 2)
        .join();
    }
    outDto = jobLogic(outDto, query);
  }
  if (
    !!ci?.length &&
    !ar?.length &&
    !pr?.length &&
    !wa?.length &&
    !st?.length &&
    !rt?.length
  ) {
    if (ci.length === 1) {
      // 市町村が1つのみ選択されていた場合
      const cityList = MasterCacheModel?.city || [];
      const prefectureList = createPrefectureList();
      const city = cityList.find((x) => x.id === getArrayFirst(ci));
      if (!city) return outDto;
      const pref = prefectureList.find((x) => x.id === city.prefId);
      if (!pref) return outDto;
      outDto.titleOrText = city.name + createBracketedText(pref.name);
    }
    outDto = jobLogic(outDto, query);
  }
  if (
    !!wa?.length &&
    !ar?.length &&
    !pr?.length &&
    !ci?.length &&
    !st?.length &&
    !rt?.length
  ) {
    if (wa.length === 1) {
      const wardList = createWandList();
      const prefectureList = createPrefectureList();
      const ward = wardList.find((x) => x.id === getArrayFirst(wa));
      if (!ward) return outDto;
      const pref = prefectureList.find((x) => x.id === ward.prefId);
      if (!pref) return outDto;
      outDto.titleOrText = ward.name + createBracketedText(pref.name);
    }
    outDto = jobLogic(outDto, query);
  }
  if (
    !!st?.length &&
    !ar?.length &&
    !pr?.length &&
    !ci?.length &&
    !wa?.length &&
    !rt?.length
  ) {
    if (st.length === 1) {
      // 駅が1つのみ選択されていた場合
      // 駅の場合
      const stationList = createStationList();
      const station = stationList.find((x) => x.id === getArrayFirst(st));
      if (!station) return outDto;
      const prefectureList = createPrefectureList();
      const pref = prefectureList.find((x) => x.id === station.prefectureId);
      if (!pref) return outDto;
      // eslint-disable-next-line no-useless-escape
      const onlySt = station.name.replace(/\(([^\)]*)\)/, '');
      outDto.titleOrText = onlySt + createBracketedText(pref.name);
    }
    outDto = jobLogic(outDto, query);
  }
  if (
    !!rt?.length &&
    !ar?.length &&
    !pr?.length &&
    !ci?.length &&
    !wa?.length &&
    !st?.length
  ) {
    if (rt.length === 1) {
      // 路線が1つのみ選択されていた場合
      outDto = setRouteSentence(outDto, rt.join());
    }
    outDto = jobLogic(outDto, query);
  }
  return outDto;
};

/**
 * 業種ドリルダウン
 * @param outDto Dto
 * @param query  AppQueryParams パラメータデータ
 * @returns outDto
 */
const indDrilldown = (outDto: Dto, query: AppQueryParams) => {
  const { ind, ar, pr, ci, wa } = query;
  if (!ind) return outDto;
  const aryIndustryL = MasterCacheModel.industry;
  if (!aryIndustryL) return outDto;
  if (ind?.length && ind.length === 1) {
    // 選択された業種が一つのみの場合
    let strInd = ind[0];
    // 小項目の一元化
    const aryIndustryS = aryIndustryL?.reduce(
      (x, y) => [...x, ...y.industrySList],
      [] as Array<IndustrySResponse>
    );
    if (strInd.indexOf('L') > -1) {
      // 職種大分類が1つのみ選択されていた場合
      // 大分類の特定
      strInd = strInd.replaceAll('L', '');
      const idln = aryIndustryL?.find((x) => strInd === x.id);
      if (!idln) return outDto;
      outDto.titleOrText = idln.name;
      outDto.text = idln.industrySList.map((x) => x.name).join();
    } else if (strInd.indexOf('S') > -1) {
      // 職種小分類が1つのみ選択されていた場合
      strInd = strInd.replaceAll('S', '');
      const idsn = aryIndustryS?.find((x) => strInd === x.id);
      if (!idsn) return outDto;
      outDto.titleOrText = idsn.name;
    }
    // 地域処理
    outDto = arLogic(outDto, ar || []);
    // 都道府県処理
    outDto = prLogic(outDto, pr || []);
    // 市区町村処理
    outDto = ciLogic(outDto, ci || []);
    // 市区処理
    outDto = waLogic(outDto, wa || []);
    // 職種処理
    outDto = jobLogic(outDto, query);
  }
  return outDto;
};

/**
 * こだわり条件処理
 * @param outDto Dto
 * @param query  AppQueryParams パラメータデータ
 * @returns outDto
 */
export const opLogic = (outDto: Dto, query: AppQueryParams) => {
  const { ha, ne, es } = query;
  const op = sortOp(ownerOp(query.op));

  if (
    !!op &&
    isOnlyForArray(op, [ha, ne, es]) &&
    dodaKodawariMap().get(op[0])?.length
  ) {
    outDto.titleOrText = `${outDto.titleOrText}${
      outDto.titleOrText ? '、' : ''
    }${dodaKodawariMap().get(op[0])}`;
  } else if (ha?.length && !op?.length && !ne?.length && !es?.length) {
    if (haSelectCheck(ha)) {
      outDto.titleOrText = `${outDto.titleOrText}${
        outDto.titleOrText ? '、' : ''
      }${dodaNenshuMap().get(ha[0])}`;
    }
  } else if (
    ne &&
    ne?.length === 1 &&
    !op?.length &&
    !ha?.length &&
    !es?.length &&
    dodaJugyoinMap().get(getArrayFirst(ne))
  ) {
    outDto.titleOrText = `${outDto.titleOrText}${
      outDto.titleOrText ? '、' : ''
    }${dodaJugyoinMap().get(ne[0])}`;
  } else if (
    es &&
    es?.length === 1 &&
    !op?.length &&
    !ha?.length &&
    !ne?.length &&
    dodaSetsuritsuMap().get(getArrayFirst(es))
  ) {
    outDto.titleOrText = `${outDto.titleOrText}${
      outDto.titleOrText ? '、' : ''
    }${dodaSetsuritsuMap().get(es[0])}`;
  } else if (
    !!op &&
    valueExistsInOp(op, 2) &&
    isEmptyArray([ha, ne, es]) &&
    dodaKodawariMap().get(op[0])?.length &&
    dodaKodawariMap().get(op[1])?.length
  ) {
    // 1軸×こだわり2軸（op×op）
    outDto.titleOrText = `${outDto.titleOrText}${
      outDto.titleOrText ? '、' : ''
    }${dodaKodawariMap().get(op[0] || '')}、${dodaKodawariMap().get(
      op[1] || ''
    )}`;
  } else if (
    !!op &&
    valueExistsInOp(op, 1) &&
    !!ha &&
    haSelectCheck(ha) &&
    isEmptyArray([ne, es])
  ) {
    const opName = dodaKodawariMap().get(op[0]);
    // 1軸×こだわり2軸（op×ha）
    outDto.titleOrText = `${outDto.titleOrText}${
      outDto.titleOrText ? '、' : ''
    }${opName ? `${opName}、` : ''}${dodaNenshuMap().get(ha[0])}`;
  }
  outDto = kLogic(outDto, query);
  return outDto;
};

/**
 * フリーワード処理
 * @param outDto Dto
 * @param query  AppQueryParams パラメータデータ
 * @returns outDto
 */
export const kLogic = (outDto: Dto, query: AppQueryParams) => {
  const { k } = query;
  if (k && outDto.titleOrText && isTwoParamPreK(query)) {
    outDto.titleOrText = `${outDto.titleOrText}、${k}`;
  }
  return outDto;
};

/**
 * 職種処理
 * @param outDto Dto
 * @param query  AppQueryParams パラメータデータ
 * @returns
 */
const jobLogic = (outDto: Dto, query: AppQueryParams) => {
  const { oc } = query;
  const occupations: JobTypeLResponse[] = MasterCacheModel?.occupation || [];

  if (oc?.length && oc.length === 1) {
    // 職種が1つのみ選択されていた場合
    const jobTypeMList = occupations?.reduce(
      (x, y) => [...x, ...y.jobTypeMList],
      [] as JobTypeMResponse[]
    );
    const jobTypeSList = jobTypeMList?.reduce(
      (x, y) => [...x, ...y.jobTypeSList],
      [] as JobTypeSResponse[]
    );
    const strOc = oc.join();
    if (strOc.indexOf('L') > -1) {
      // 職種大分類が1つのみ選択されていた場合
      const wocl = occupations?.find(
        (x) => strOc.indexOf(x.searchJobTypeId) > -1
      );
      outDto.titleOrText = `${outDto.titleOrText}${
        outDto.titleOrText ? '、' : ''
      }${wocl?.searchJobTypeName || ''}`;
    } else if (strOc.indexOf('M') > -1) {
      // 職種中分類が1つのみ選択されていた場合
      const wocm = jobTypeMList?.find(
        (x) => strOc.indexOf(x.searchJobTypeId) > -1
      );
      outDto.titleOrText = `${outDto.titleOrText}${
        outDto.titleOrText ? '、' : ''
      }${wocm?.searchJobTypeName || ''}`;
    } else if (strOc.indexOf('S') > -1) {
      // 職種小分類が1つのみ選択されていた場合
      const wocs = jobTypeSList?.find(
        (x) => strOc.indexOf(x.searchJobTypeId) > -1
      );
      outDto.titleOrText = `${outDto.titleOrText}${
        outDto.titleOrText ? '、' : ''
      }${wocs?.searchJobTypeName || ''}`;
    }
  }
  outDto = opLogic(outDto, query);
  return outDto;
};

/**
 * 地域処理
 * @param outDto Dto
 * @param ar Item[] 市区郡
 * @returns outDto
 */
const arLogic = (outDto: Dto, ar: Item): Dto => {
  const areaList = MasterCacheModel?.area || [];
  if (!!ar?.length && ar.length === 1) {
    // 地域が1つのみ選択されていた場合
    const area = areaList.find((x) => x.id === getArrayFirst(ar));
    if (!area) return outDto;
    outDto.titleOrText = `${outDto.titleOrText}${
      outDto.titleOrText ? '、' : ''
    }${area.name}`;
  }
  return outDto;
};

/**
 * 都道府県処理
 * @param outDto Dto
 * @param pr Item[] 市区郡
 * @returns outDto
 */
const prLogic = (outDto: Dto, pr: Item): Dto => {
  if (!!pr?.length && pr.length === 1) {
    // 都道府県が1つのみ選択されていた場合
    const prefectureList = createPrefectureList();
    const pref = prefectureList.find((x) => x.id === getArrayFirst(pr));
    if (!pref) return outDto;
    outDto.titleOrText = `${outDto.titleOrText}${
      outDto.titleOrText ? '、' : ''
    }${pref.name}`;
  }
  return outDto;
};

/**
 * 市区郡処理
 * @param outDto Dto
 * @param ci Item[] 市区郡
 * @returns outDto
 */
const ciLogic = (outDto: Dto, ci: Item): Dto => {
  if (ci.length === 1) {
    // 市町村が1つのみ選択されていた場合
    const cityList = MasterCacheModel?.city || [];
    const prefectureList = createPrefectureList();
    const city = cityList.find((x) => x.id === getArrayFirst(ci));
    if (!city) return outDto;
    const pref = prefectureList.find((x) => x.id === city.prefId);
    if (!pref) return outDto;
    outDto.titleOrText = `${outDto.titleOrText}${
      outDto.titleOrText ? '、' : ''
    }${city.name}${createBracketedText(pref.name)}`;
  }
  return outDto;
};

/**
 * 政令指定都市処理
 * @param outDto Dto
 * @param wa Item[] 政令指定都市
 * @returns outDto
 */
const waLogic = (outDto: Dto, wa: Item): Dto => {
  if (wa.length === 1) {
    const wardList = createWandList();
    const prefectureList = createPrefectureList();
    const ward = wardList.find((x) => x.id === getArrayFirst(wa));
    if (!ward) return outDto;
    const pref = prefectureList.find((x) => x.id === ward.prefId);
    if (!pref) return outDto;
    outDto.titleOrText = `${outDto.titleOrText}${
      outDto.titleOrText ? '、' : ''
    }${ward.name}${createBracketedText(pref.name)}`;
  }
  return outDto;
};

/**
 * 駅処理
 * @param outDto Dto
 * @param rt Item[] 路線リスト
 * @returns outDto
 */
const setRouteSentence = (outDto: Dto, rt: string): Dto => {
  const routeCompanyList = MasterCacheModel?.route || [];
  const routeList = createRouteList();
  const route = routeList.find((x) => x.id === rt);
  if (!route) return outDto;
  const company = routeCompanyList.find((x) => x.id === route.companyId);
  if (!company) return outDto;
  // マスタに登録されていない路線コードの場合
  const convertedRouteName = createRouteWithCompName(route.name, company.name);
  const threeDspstnnm = route.stationList
    .slice(0, 2)
    .map((x) => x.name)
    .join();
  outDto.titleOrText = convertedRouteName;
  outDto.text = threeDspstnnm;
  return outDto;
};

/**
 * 路線名と鉄道会社名をマージする。
 * @param routeName
 * @param compName
 */
export const createRouteWithCompName = (routeName, compName): string => {
  if (!routeName?.length) return '';
  if (!compName?.length) return routeName;
  return `${routeName}-${compName}`;
};

/**
 * 第一引数が一つ、第二引数が一つもない状態はtrueを返す
 * @param listOrString
 * @param itemAry
 * @returns Boolean
 */
export const isOnlyForArray = (
  listOrString: Item | string | undefined,
  itemAry: (string | Item | undefined)[]
) => {
  if (Array.isArray(listOrString)) {
    if (listOrString.length !== 1) return false;
  } else if (!listOrString) return false;
  return !itemAry?.some((x) => x?.length);
};

/**
 * こだわり条件Map
 */
export const dodaKodawariMap = () => {
  const map = new Map();
  const opKeyList = Object.keys(OP_SEARCH_ITEMS);
  opKeyList.forEach((key) => {
    if (OP_SEARCH_ITEMS[key].name?.indexOf('代') === 2) {
      map.set(
        OP_SEARCH_ITEMS[key].id,
        `社員の平均年齢${OP_SEARCH_ITEMS[key].name}`
      );
    } else {
      map.set(
        OP_SEARCH_ITEMS[key].id,

        OP_SEARCH_ITEMS[key].name
      );
    }
  });
  map.set(ID_OTHERS, 'その他（FCオーナー・業務委託など）');
  return map;
};

/**
 * 年収Map
 */
export const dodaNenshuMap = () => {
  const map = new Map();
  HA_SEARCH_ITEMS.forEach((item) => map.set(item.id, item.seoText));
  return map;
};

/**
 * 従業員Map
 */
export const dodaJugyoinMap = () => {
  const map = new Map();
  NE_SEARCH_ITEMS.forEach((item) => map.set(item.id, item.seoText));
  return map;
};

/**
 * 設立Map
 */
export const dodaSetsuritsuMap = () => {
  const map = new Map();
  ES_SEARCH_ITEMS.forEach((item) => map.set(item.id, item.seoText));
  return map;
};

/**
 * 配列一つ目を取得する。
 * TODO: 配列取得をundefinedで悩んだ為の実装。
 * @param ary
 **/
export const getArrayFirst = (ary: Array<string> | undefined): string =>
  ary?.length ? ary[0] : '';

/**
 * htmlタグの修正
 * @param str
 */
export const sanitize = (str: string) => {
  str = str.replaceAll('&', '&amp;');
  str = str.replaceAll('<', '&lt;');
  str = str.replaceAll('>', '&gt;');
  str = str.replaceAll('"', '&quot;');
  str = str.replaceAll("'", '&#39;');
  return str;
};

/**
 * こだわり条件が二つある場合
 * @param query
 * @returns
 */
export const isTwoParamOpOp = (query: AppQueryParams) => {
  const { k, ind, oc, ar, pr, ci, wa, rt, st, ha, ne, es } = query;
  const op = ownerOp(query.op);
  const otherParam = [k, ind, oc, ar, pr, ci, wa, rt, st, ha, ne, es];
  if (!op?.length) return false;
  return (
    op.length === 2 &&
    dodaKodawariMap().has(op[0]) &&
    dodaKodawariMap().has(op[1]) &&
    !otherParam.some((x) => x?.length)
  );
};

/**
 * こだわり条件が二つある場合
 * @param query
 * @returns
 */
export const isTwoParamOpHa = (query: AppQueryParams) => {
  const { k, ind, oc, ar, pr, ci, wa, rt, st, ha, ne, es } = query;
  const op = ownerOp(query.op);
  const otherParam = [k, ind, oc, ar, pr, ci, wa, rt, st, ne, es];
  return (
    ha?.length === 2 &&
    op?.length === 1 &&
    dodaKodawariMap().has(getArrayFirst(op)) &&
    dodaNenshuMap().has(getArrayFirst(ha)) &&
    !otherParam.some((x) => x?.length)
  );
};

/**
 * こだわり条件が三つ（op,op,op）ある場合
 * @param query
 * @returns
 */
export const isThreeParamOpOpOp = (query: AppQueryParams) => {
  const { k, ind, oc, ar, pr, ci, wa, rt, st, ha, ne, es } = query;
  const op = ownerOp(query.op);
  const otherParam = [k, ind, oc, ar, pr, ci, wa, rt, st, ha, ne, es];
  if (!op?.length) return false;
  return (
    op.length === 3 &&
    dodaKodawariMap().has(op[0]) &&
    dodaKodawariMap().has(op[1]) &&
    dodaKodawariMap().has(op[2]) &&
    !otherParam.some((x) => x?.length)
  );
};

/**
 * こだわり条件が三つ（op,op,ha）ある場合
 * @param query
 * @returns
 */
export const isThreeParamOpOpHa = (query: AppQueryParams) => {
  const { k, ind, oc, ar, pr, ci, wa, rt, st, ha, ne, es } = query;
  const op = ownerOp(query.op);
  const otherParam = [k, ind, oc, ar, pr, ci, wa, rt, st, ne, es];
  return (
    ha?.length === 2 &&
    op?.length === 2 &&
    dodaKodawariMap().has(op[0]) &&
    dodaKodawariMap().has(op[1]) &&
    dodaNenshuMap().has(getArrayFirst(ha)) &&
    !otherParam.some((x) => x?.length)
  );
};

/**
 * 都道府県リストの一元化
 */
export const createPrefectureList = (): PrefOfSearchModal[] =>
  MasterCacheModel?.area?.reduce(
    (x, y) => [...x, ...y.prefectureList],
    [] as PrefOfSearchModal[]
  ) || [];

/**
 * 政令指定都市リストの一元化
 */
export const createWandList = (): SeoTextWardResponse[] =>
  MasterCacheModel?.city?.reduce(
    (x, y) => [
      ...x,
      ...y.wardList.map((z) => ({
        ...z,
        cityId: y.id,
        prefId: y.prefId,
      })),
    ],
    [] as SeoTextWardResponse[]
  ) || [];

/**
 * 路線リストの一元化
 */
export const createRouteList = (): SeoTextRouteResponse[] =>
  MasterCacheModel?.route?.reduce(
    (x, y) => [
      ...x,
      ...y.routeList.map((z) => ({
        ...z,
        companyId: y.id,
      })),
    ],
    [] as SeoTextRouteResponse[]
  ) || [];

/**
 * 駅リストの一元化
 */
export const createStationList = (): StationResponse[] =>
  createRouteList().reduce(
    (x, y) => [...x, ...y.stationList],
    [] as StationResponse[]
  ) || [];

const createBracketedText = (text: string) => {
  return SIGN_FULLSIZE_KAKO_ST + text + SIGN_FULLSIZE_KAKO_ED;
};
